import { useEffect, useMemo, useRef, useState } from 'react';
import { Users, Plus, Calculator, FileDown, FileUp, Save } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney, monthlyPayeFor } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { localYmd } from '@/lib/journal-postings';

interface Employee {
  id: string;
  business_id: string;
  name: string;
  national_id: string | null;
  position: string | null;
  start_date: string | null;
  end_date: string | null;
  basic_salary: number;
  allowances: { transport?: number; housing?: number; other?: number };
  deductions: { pension_employee?: number; medical_aid?: number; loan?: number; other?: number };
  benefits: Record<string, unknown>;
  is_active: boolean;
}

interface Payslip {
  employee: Employee;
  basic: number;
  transport: number;
  housing: number;
  otherAllow: number;
  gross: number;
  paye: number;
  pension: number;
  medical: number;
  loan: number;
  otherDed: number;
  totalDeductions: number;
  net: number;
}

function buildPayslip(e: Employee, region: string = 'BW'): Payslip {
  const basic = e.basic_salary || 0;
  const transport = e.allowances?.transport ?? 0;
  const housing = e.allowances?.housing ?? 0;
  const otherAllow = e.allowances?.other ?? 0;
  const gross = basic + transport + housing + otherAllow;

  const pension = e.deductions?.pension_employee ?? 0;
  const medical = e.deductions?.medical_aid ?? 0;
  const loan = e.deductions?.loan ?? 0;
  const otherDed = e.deductions?.other ?? 0;

  // PAYE: taxable income = gross less pension contribution.
  // Housing allowance is fully taxable; transport partially exempt up to a
  // small threshold — for demo purposes we treat both as taxable.
  const taxable = Math.max(0, gross - pension);
  const paye = monthlyPayeFor(taxable, region);

  const totalDeductions = paye + pension + medical + loan + otherDed;
  const net = gross - totalDeductions;

  return {
    employee: e,
    basic,
    transport,
    housing,
    otherAllow,
    gross,
    paye,
    pension,
    medical,
    loan,
    otherDed,
    totalDeductions,
    net: round2(net),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

const monthName = (month: number) =>
  ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month] ?? '';

export default function Payroll() {
  const business = useAuthStore((s) => s.business)!;
  const session = useAuthStore((s) => s.session);
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const toast = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Employee | null>(null);

  const today = new Date();
  const [period, setPeriod] = useState({
    month: today.getMonth(),
    year: today.getFullYear(),
  });

  const [form, setForm] = useState({
    name: '',
    national_id: '',
    position: '',
    basic: 0,
    transport: 0,
    housing: 0,
    otherAllow: 0,
    pension: 0,
    medical: 0,
    loan: 0,
    otherDed: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const { data } = await supabase
      .from('employees')
      .select('*')
      .eq('business_id', business.id)
      .order('name');
    setEmployees((data ?? []) as Employee[]);
  }

  const payslips = useMemo(
    () => employees.filter((e) => e.is_active).map((e) => buildPayslip(e, regionCode)),
    [employees, regionCode],
  );

  const totals = useMemo(() => {
    const t = { gross: 0, paye: 0, pension: 0, medical: 0, loan: 0, other: 0, net: 0 };
    for (const p of payslips) {
      t.gross += p.gross;
      t.paye += p.paye;
      t.pension += p.pension;
      t.medical += p.medical;
      t.loan += p.loan;
      t.other += p.otherDed;
      t.net += p.net;
    }
    return t;
  }, [payslips]);

  async function create() {
    if (!form.name || !form.basic) {
      toast.show('Name and basic salary required', { tone: 'warning' });
      return;
    }
    const { error } = await supabase.from('employees').insert({
      business_id: business.id,
      name: form.name,
      national_id: form.national_id || null,
      position: form.position || null,
      start_date: new Date().toISOString().slice(0, 10),
      end_date: null,
      basic_salary: form.basic,
      allowances: {
        transport: form.transport,
        housing: form.housing,
        other: form.otherAllow,
      },
      deductions: {
        pension_employee: form.pension,
        medical_aid: form.medical,
        loan: form.loan,
        other: form.otherDed,
      },
      benefits: {},
      is_active: true,
    });
    if (error) {
      toast.show(`Failed to add employee: ${error.message}`, { tone: 'error' });
      return;
    }
    toast.show('Employee added.', { tone: 'success' });
    setOpen(false);
    setForm({
      name: '',
      national_id: '',
      position: '',
      basic: 0,
      transport: 0,
      housing: 0,
      otherAllow: 0,
      pension: 0,
      medical: 0,
      loan: 0,
      otherDed: 0,
    });
    void load();
  }

  async function uploadCsv(file: File) {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      toast.show('CSV is empty.', { tone: 'warning' });
      return;
    }
    const headerLine = lines[0] ?? '';
    const headers = parseCsvRow(headerLine).map((h) => h.toLowerCase().trim());
    const idx = (key: string) => headers.findIndex((h) => h === key.toLowerCase());

    const iName = idx('Employee');
    const iNid = idx('NID');
    const iPos = idx('Position');
    const iBasic = idx('Basic');
    const iTransport = idx('Transport');
    const iHousing = idx('Housing');
    const iOther = idx('Other Allow');
    const iPension = idx('Pension');
    const iMedical = idx('Medical');
    const iLoan = idx('Loan');
    const iOtherDed = idx('Other Ded');

    if (iName < 0 || iBasic < 0) {
      toast.show('CSV must have at least Employee + Basic columns.', { tone: 'error' });
      return;
    }

    const num = (s: string | undefined) => (s ? Number(s) || 0 : 0);
    const records = lines
      .slice(1)
      .map((line) => {
        const cols = parseCsvRow(line);
        return {
          business_id: business.id,
          name: cols[iName]?.trim() ?? '',
          national_id: iNid >= 0 ? cols[iNid]?.trim() || null : null,
          position: iPos >= 0 ? cols[iPos]?.trim() || null : null,
          start_date: new Date().toISOString().slice(0, 10),
          end_date: null,
          basic_salary: num(cols[iBasic]),
          allowances: {
            transport: iTransport >= 0 ? num(cols[iTransport]) : 0,
            housing: iHousing >= 0 ? num(cols[iHousing]) : 0,
            other: iOther >= 0 ? num(cols[iOther]) : 0,
          },
          deductions: {
            pension_employee: iPension >= 0 ? num(cols[iPension]) : 0,
            medical_aid: iMedical >= 0 ? num(cols[iMedical]) : 0,
            loan: iLoan >= 0 ? num(cols[iLoan]) : 0,
            other: iOtherDed >= 0 ? num(cols[iOtherDed]) : 0,
          },
          benefits: {},
          is_active: true,
        };
      })
      .filter((r) => r.name);

    if (records.length === 0) {
      toast.show('No employees found in CSV.', { tone: 'warning' });
      return;
    }

    const { error } = await supabase.from('employees').insert(records);
    if (error) {
      toast.show(`Upload failed: ${error.message}`, { tone: 'error' });
      return;
    }
    toast.show(`Imported ${records.length} employee${records.length === 1 ? '' : 's'} ✓`, {
      tone: 'success',
    });
    void load();
  }

  function parseCsvRow(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuotes) {
        if (c === '"' && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (c === '"') {
          inQuotes = false;
        } else {
          cur += c;
        }
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        out.push(cur);
        cur = '';
      } else {
        cur += c;
      }
    }
    out.push(cur);
    return out;
  }

  async function postPayrollToLedger() {
    // Build a single journal entry for the run:
    //   Dr Salaries & Wages (5100)  total gross
    //   Cr PAYE Payable (2200)      total paye
    //   Cr Cash (1000)              net + non-PAYE deductions (held by club for now)
    const periodRef = `Payroll ${monthName(period.month)} ${period.year}`;
    const { data: existingPayroll } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('business_id', business.id)
      .eq('source', 'payroll')
      .eq('reference', periodRef)
      .maybeSingle();
    if (existingPayroll) {
      toast.show('Payroll already posted for this period.', { tone: 'warning' });
      return;
    }

    const accounts = await supabase.from('accounts').select('*').eq('business_id', business.id);
    const list = (accounts.data ?? []) as Array<{ id: string; gl_code: string; name: string }>;
    const wages = list.find((a) => a.gl_code === '5100');
    const paye = list.find((a) => a.gl_code === '2200');
    const cash = list.find((a) => a.gl_code === '1000');
    const deductionsAcc = list.find((a) => a.gl_code === '2210'); // Payroll Deductions Payable
    if (!wages || !paye || !cash) {
      toast.show('Missing wage / PAYE / cash GL accounts.', { tone: 'error' });
      return;
    }

    // Date the run inside the dashboard's "this month" window (month-start → NOW):
    // a current-month run dates today so it shows immediately; a past-period run
    // dates that month-end. A future-of-today month-end date was being excluded
    // from the dashboard — the "payroll posted but dashboard still P0.00" bug.
    const periodEnd = new Date(period.year, period.month + 1, 0);
    const now = new Date();
    // localYmd, NOT toISOString: we're UTC+2, so toISOString() on a local
    // month-end (or just after local midnight) dates the entry a day early —
    // on the 1st that lands it in the PREVIOUS month and it vanishes from
    // the dashboard's month window.
    const date = localYmd(periodEnd < now ? periodEnd : now);
    const { data: entry } = await supabase
      .from('journal_entries')
      .insert({
        business_id: business.id,
        entry_date: date,
        reference: periodRef,
        description: `Payroll run for ${payslips.length} employees`,
        source: 'payroll',
        is_posted: true,
        posted_at: new Date().toISOString(),
        created_by: session?.user.id ?? null,
      })
      .select()
      .single();
    if (!entry) {
      toast.show('Could not post payroll entry', { tone: 'error' });
      return;
    }

    const eid = (entry as { id: string }).id;
    // Non-PAYE deductions (pension/medical/loan/other) are WITHHELD, not paid
    // in cash — credit them to a liability the employer remits later, and credit
    // Cash only with the actual net pay disbursed (Round-2 finding #7). If the
    // 2210 account isn't present (pre-migration), fall back to the old single
    // cash credit so the entry still balances.
    const otherDeductions = round2(totals.pension + totals.medical + totals.loan + totals.other);
    const lines: Array<{
      entry_id: string;
      account_id: string;
      debit: number;
      credit: number;
      vat_amount: number;
      vat_rate: number;
      description: string;
    }> = [
      {
        entry_id: eid,
        account_id: wages.id,
        debit: round2(totals.gross),
        credit: 0,
        vat_amount: 0,
        vat_rate: 0,
        description: `Gross wages ${monthName(period.month)} ${period.year}`,
      },
      {
        entry_id: eid,
        account_id: paye.id,
        debit: 0,
        credit: round2(totals.paye),
        vat_amount: 0,
        vat_rate: 0,
        description: `PAYE withholding (${taxAuth})`,
      },
    ];
    if (otherDeductions > 0 && deductionsAcc) {
      lines.push({
        entry_id: eid,
        account_id: deductionsAcc.id,
        debit: 0,
        credit: otherDeductions,
        vat_amount: 0,
        vat_rate: 0,
        description: 'Employee deductions withheld (pension / medical / loan / other)',
      });
    }
    const cashCredit =
      otherDeductions > 0 && deductionsAcc
        ? round2(totals.net) // true net pay disbursed
        : round2(totals.gross - totals.paye); // fallback: lump (no 2210 account)
    lines.push({
      entry_id: eid,
      account_id: cash.id,
      debit: 0,
      credit: cashCredit,
      vat_amount: 0,
      vat_rate: 0,
      description: 'Net pay disbursed',
    });

    const { error: linesErr } = await supabase.from('journal_lines').insert(lines);
    // Lines failed but the header committed — delete the orphan so it can't
    // corrupt the trial balance (Round-2 finding #1).
    if (linesErr) {
      await supabase.from('journal_entries').delete().eq('id', eid);
      toast.show(`Could not post payroll lines: ${linesErr.message}`, { tone: 'error' });
      return;
    }

    toast.show('Payroll posted to ledger ✓', { tone: 'success' });
  }

  function downloadCsv() {
    const rows = [
      [
        'Employee',
        'NID',
        'Position',
        'Basic',
        'Transport',
        'Housing',
        'Other Allow',
        'Gross',
        'PAYE',
        'Pension',
        'Medical',
        'Loan',
        'Other Ded',
        'Total Deductions',
        'Net',
      ].join(','),
      ...payslips.map((p) =>
        [
          q(p.employee.name),
          p.employee.national_id ?? '',
          q(p.employee.position ?? ''),
          p.basic,
          p.transport,
          p.housing,
          p.otherAllow,
          p.gross,
          p.paye,
          p.pension,
          p.medical,
          p.loan,
          p.otherDed,
          p.totalDeductions,
          p.net,
        ].join(','),
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_${period.year}_${monthName(period.month)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function q(s: string): string {
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Users size={22} /> Payroll
        </h1>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
          Add employee
        </Button>
      </div>

      {/* Period selector */}
      <Card compact className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-muted text-xs">Pay period</p>
            <p className="font-medium">
              {monthName(period.month)} {period.year}
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={period.month}
              onChange={(e) => setPeriod({ ...period, month: Number(e.target.value) || 0 })}
              className="rounded-btn border-line border px-3 py-1.5 text-sm"
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <option key={i} value={i}>
                  {monthName(i)}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={period.year}
              onChange={(e) => setPeriod({ ...period, year: Number(e.target.value) || 0 })}
              className="rounded-btn border-line w-20 border px-2 py-1.5 font-mono text-sm"
            />
          </div>
        </div>
      </Card>

      {/* Totals */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card compact>
          <p className="text-muted text-xs">Gross wage bill</p>
          <p className="tnum font-mono text-lg">{formatMoney(totals.gross, regionCode)}</p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">PAYE due to {taxAuth}</p>
          <p className="tnum text-red font-mono text-lg">{formatMoney(totals.paye, regionCode)}</p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">Total deductions</p>
          <p className="tnum font-mono text-lg">
            {formatMoney(
              totals.paye + totals.pension + totals.medical + totals.loan + totals.other,
              regionCode,
            )}
          </p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">Net to pay</p>
          <p className="tnum text-green font-mono text-lg">{formatMoney(totals.net, regionCode)}</p>
        </Card>
      </div>

      {/* Payslip table */}
      {payslips.length === 0 ? (
        <Card className="py-10 text-center">
          <Users size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted mb-4">No employees yet — add your first one.</p>
          <Button onClick={() => setOpen(true)} leftIcon={<Plus size={16} />}>
            Add employee
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {payslips.map((p) => (
            <Card key={p.employee.id} compact>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{p.employee.name}</p>
                    <Pill tone="neutral">{p.employee.position ?? '—'}</Pill>
                  </div>
                  <p className="text-muted text-xs">
                    Basic {formatMoney(p.basic, regionCode)} · Transport{' '}
                    {formatMoney(p.transport, regionCode)}
                    {p.housing > 0 && ` · Housing ${formatMoney(p.housing, regionCode)}`}
                  </p>
                  <p className="text-muted text-xs">
                    PAYE {formatMoney(p.paye, regionCode)} · Pension{' '}
                    {formatMoney(p.pension, regionCode)}
                    {p.medical > 0 && ` · Medical ${formatMoney(p.medical, regionCode)}`}
                    {p.loan > 0 && ` · Loan ${formatMoney(p.loan, regionCode)}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-muted text-xs">Net</p>
                  <p className="tnum text-green font-mono text-base font-semibold">
                    {formatMoney(p.net, regionCode)}
                  </p>
                  <button
                    onClick={() => setSelected(p.employee)}
                    className="text-accent mt-1 block w-full text-right text-[11px] underline"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void uploadCsv(f);
          e.target.value = '';
        }}
      />

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Button
          variant="secondary"
          leftIcon={<FileUp size={14} />}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload CSV
        </Button>
        <Button
          variant="secondary"
          leftIcon={<FileDown size={14} />}
          onClick={downloadCsv}
          disabled={payslips.length === 0}
        >
          Download CSV
        </Button>
        <Button
          leftIcon={<Calculator size={14} />}
          onClick={postPayrollToLedger}
          disabled={payslips.length === 0}
        >
          Post to ledger
        </Button>
      </div>

      {/* Add employee */}
      <Modal open={open} onClose={() => setOpen(false)} title="Add employee">
        <div className="space-y-3">
          <Input
            label="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="National ID"
            value={form.national_id}
            onChange={(e) => setForm({ ...form, national_id: e.target.value })}
          />
          <Input
            label="Position"
            value={form.position}
            onChange={(e) => setForm({ ...form, position: e.target.value })}
          />
          <Input
            label="Basic salary (monthly)"
            type="number"
            numeric
            leftAddon="P"
            value={form.basic || ''}
            onChange={(e) => setForm({ ...form, basic: Number(e.target.value) || 0 })}
          />
          <p className="text-muted text-xs font-semibold uppercase tracking-wider">Allowances</p>
          <div className="grid grid-cols-3 gap-2">
            <Input
              label="Transport"
              type="number"
              numeric
              leftAddon="P"
              value={form.transport || ''}
              onChange={(e) => setForm({ ...form, transport: Number(e.target.value) || 0 })}
            />
            <Input
              label="Housing"
              type="number"
              numeric
              leftAddon="P"
              value={form.housing || ''}
              onChange={(e) => setForm({ ...form, housing: Number(e.target.value) || 0 })}
            />
            <Input
              label="Other"
              type="number"
              numeric
              leftAddon="P"
              hint="Acting, bonus, etc."
              value={form.otherAllow || ''}
              onChange={(e) => setForm({ ...form, otherAllow: Number(e.target.value) || 0 })}
            />
          </div>
          <p className="text-muted text-xs font-semibold uppercase tracking-wider">Deductions</p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Pension"
              type="number"
              numeric
              leftAddon="P"
              value={form.pension || ''}
              onChange={(e) => setForm({ ...form, pension: Number(e.target.value) || 0 })}
            />
            <Input
              label="Medical aid"
              type="number"
              numeric
              leftAddon="P"
              value={form.medical || ''}
              onChange={(e) => setForm({ ...form, medical: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Loan repayment"
              type="number"
              numeric
              leftAddon="P"
              value={form.loan || ''}
              onChange={(e) => setForm({ ...form, loan: Number(e.target.value) || 0 })}
            />
            <Input
              label="Other deduction"
              type="number"
              numeric
              leftAddon="P"
              hint="Garnishee, advance, etc."
              value={form.otherDed || ''}
              onChange={(e) => setForm({ ...form, otherDed: Number(e.target.value) || 0 })}
            />
          </div>
          <Button full onClick={create} leftIcon={<Save size={16} />}>
            Save
          </Button>
        </div>
      </Modal>

      {/* Edit employee — quick edit for salary + allowances + deductions */}
      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `Edit ${selected.name}` : ''}
      >
        {selected && (
          <EmployeeEdit
            employee={selected}
            onSaved={() => {
              setSelected(null);
              void load();
            }}
            onCancel={() => setSelected(null)}
          />
        )}
      </Modal>
    </main>
  );
}

function EmployeeEdit({
  employee,
  onSaved,
  onCancel,
}: {
  employee: Employee;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [basic, setBasic] = useState(employee.basic_salary);
  const [transport, setTransport] = useState(employee.allowances?.transport ?? 0);
  const [housing, setHousing] = useState(employee.allowances?.housing ?? 0);
  const [otherAllow, setOtherAllow] = useState(employee.allowances?.other ?? 0);
  const [pension, setPension] = useState(employee.deductions?.pension_employee ?? 0);
  const [medical, setMedical] = useState(employee.deductions?.medical_aid ?? 0);
  const [loan, setLoan] = useState(employee.deductions?.loan ?? 0);
  const [otherDed, setOtherDed] = useState(employee.deductions?.other ?? 0);
  const [active, setActive] = useState(employee.is_active);

  async function save() {
    await supabase
      .from('employees')
      .update({
        basic_salary: basic,
        allowances: { transport, housing, other: otherAllow },
        deductions: {
          pension_employee: pension,
          medical_aid: medical,
          loan,
          other: otherDed,
        },
        is_active: active,
      })
      .eq('id', employee.id);
    toast.show('Employee updated.', { tone: 'success' });
    onSaved();
  }

  return (
    <div className="space-y-3">
      <Input
        label="Basic salary"
        type="number"
        numeric
        leftAddon="P"
        value={basic || ''}
        onChange={(e) => setBasic(Number(e.target.value) || 0)}
      />
      <p className="text-muted text-xs font-semibold uppercase tracking-wider">Allowances</p>
      <div className="grid grid-cols-3 gap-2">
        <Input
          label="Transport"
          type="number"
          numeric
          leftAddon="P"
          value={transport || ''}
          onChange={(e) => setTransport(Number(e.target.value) || 0)}
        />
        <Input
          label="Housing"
          type="number"
          numeric
          leftAddon="P"
          value={housing || ''}
          onChange={(e) => setHousing(Number(e.target.value) || 0)}
        />
        <Input
          label="Other"
          type="number"
          numeric
          leftAddon="P"
          value={otherAllow || ''}
          onChange={(e) => setOtherAllow(Number(e.target.value) || 0)}
        />
      </div>
      <p className="text-muted text-xs font-semibold uppercase tracking-wider">Deductions</p>
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="Pension"
          type="number"
          numeric
          leftAddon="P"
          value={pension || ''}
          onChange={(e) => setPension(Number(e.target.value) || 0)}
        />
        <Input
          label="Medical"
          type="number"
          numeric
          leftAddon="P"
          value={medical || ''}
          onChange={(e) => setMedical(Number(e.target.value) || 0)}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="Loan"
          type="number"
          numeric
          leftAddon="P"
          value={loan || ''}
          onChange={(e) => setLoan(Number(e.target.value) || 0)}
        />
        <Input
          label="Other"
          type="number"
          numeric
          leftAddon="P"
          value={otherDed || ''}
          onChange={(e) => setOtherDed(Number(e.target.value) || 0)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active employee (include in payroll)
      </label>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={save} leftIcon={<Save size={16} />}>
          Save
        </Button>
      </div>
    </div>
  );
}
